import { useEffect, useMemo, useState } from 'react'
import { Amplify, Auth, I18n } from 'aws-amplify'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { translations, useAuthenticator } from '@aws-amplify/ui-react'
import { useNavigate, useLocation } from 'react-router'

import AivyLogo from '../assets/images/logo.png'
import awsExports from '../aws-exports'
import Button from '../components/Button'
import { withGraphqlApi } from '../helper/graphql-api'

Amplify.configure(withGraphqlApi(awsExports))
I18n.putVocabularies(translations)
I18n.setLanguage('de')

const EMAIL_REGEX = /^[\w-+.]+@([\w-]+\.)+[\w-]{2,}$/

const inputClass =
  'block w-full px-3 rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'

const SignIn = () => {
  const { route } = useAuthenticator((context) => [context.route])
  const location = useLocation()
  const navigate = useNavigate()
  const from = location.state?.from?.pathname || '/'
  const reason = location.state?.reason

  const [stage, setStage] = useState('ENTER_EMAIL')
  const [cognitoUser, setCognitoUser] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (route === 'authenticated') {
      navigate(from, { replace: true })
    }
  }, [route, navigate, from])

  const initialError = useMemo(() => {
    if (reason === 'not_admin') {
      return 'Dein Account hat keine OS-Berechtigung. Bitte mit einem Admin-Account anmelden.'
    }
    return null
  }, [reason])

  useEffect(() => {
    if (initialError) setError(initialError)
  }, [initialError])

  const emailSchema = Yup.object({
    email: Yup.string()
      .matches(EMAIL_REGEX, 'Keine valide E-Mail-Adresse')
      .required('E-Mail-Adresse erforderlich')
  })

  const codeSchema = Yup.object({
    code: Yup.string()
      .matches(/^\d{4,6}$/, 'Code ist 4–6-stellig')
      .required('Code erforderlich')
  })

  const requestCode = async ({ email }, { setSubmitting }) => {
    setError(null)
    try {
      Auth.configure({ authenticationFlowType: 'CUSTOM_AUTH' })
      const user = await Auth.signIn(email.toLowerCase())
      setCognitoUser(user)
      setStage('ENTER_CODE')
    } catch (err) {
      console.error(err) // eslint-disable-line
      if (err?.code === 'UserNotFoundException') {
        setError('Kein Account mit dieser E-Mail-Adresse gefunden.')
      } else if (err?.code === 'UserLambdaValidationException') {
        setError(
          'Anmeldung aktuell nicht möglich. Bitte später erneut versuchen.'
        )
      } else {
        setError(err?.message || 'Anmeldung fehlgeschlagen.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const verifyCode = async ({ code }, { setSubmitting, resetForm }) => {
    setError(null)
    try {
      const next = await Auth.sendCustomChallengeAnswer(
        cognitoUser,
        code.trim()
      )
      // Bei falschem Code wirft Cognito nicht — der User behält
      // challengeName === 'CUSTOM_CHALLENGE'. Die zurückgegebene
      // Referenz hat eine neue Session und MUSS für den nächsten
      // Versuch genutzt werden, sonst → "Invalid session for the user".
      if (next?.challengeName === 'CUSTOM_CHALLENGE') {
        setCognitoUser(next)
        resetForm({ values: { code: '' } })
        setError('Code ungültig. Bitte erneut eingeben.')
        return
      }
      await Auth.currentSession()
      // Der Amplify-UI-Provider (ui 5.x) wechselt auf ein Hub-'signIn'
      // nicht nach 'authenticated', er ruft nur onSignIn auf. Die Route
      // bliebe auf 'signIn', man säße trotz gültiger Tokens auf /login.
      // Erst beim Laden prüft er die Session per getCurrentUser, darum
      // hart auf das Ziel navigieren.
      window.location.replace(from)
    } catch (err) {
      console.error(err) // eslint-disable-line
      // NotAuthorizedException = Session verbraucht (3 Fehlversuche
      // oder >3 Minuten alt). Zurück auf Email-Stage für neuen Code.
      setError('Sitzung abgelaufen. Bitte neuen Code anfordern.')
      setStage('ENTER_EMAIL')
      setCognitoUser(null)
    } finally {
      setSubmitting(false)
    }
  }

  const restart = () => {
    setError(null)
    setCognitoUser(null)
    setStage('ENTER_EMAIL')
  }

  return (
    <div className='flex w-full h-screen items-center justify-center bg-paleGrey px-4'>
      <div className='w-full max-w-sm bg-white rounded-lg shadow-xl p-6'>
        <div className='flex flex-col py-4 items-center'>
          <img src={AivyLogo} alt='Aivy' className='w-40 h-auto' />
        </div>

        {stage === 'ENTER_EMAIL' && (
          <Formik
            initialValues={{ email: '' }}
            onSubmit={requestCode}
            validationSchema={emailSchema}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form className='mt-4 space-y-4'>
                <div>
                  <label
                    htmlFor='email'
                    className='block text-sm font-medium text-gray-700 mb-1'
                  >
                    E-Mail-Adresse
                  </label>
                  <Field
                    type='email'
                    name='email'
                    id='email'
                    autoComplete='email'
                    autoFocus
                    className={inputClass}
                    placeholder='admin@aivy.app'
                  />
                  {errors.email && touched.email ? (
                    <div className='text-xs text-red-600 mt-1'>
                      {errors.email}
                    </div>
                  ) : null}
                </div>

                {error && <div className='text-xs text-red-600'>{error}</div>}

                <Button.PrimaryLG
                  text={isSubmitting ? 'lädt ...' : 'Code anfordern'}
                  fullWidth
                  type='submit'
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />

                <p className='text-xs text-gray-500 text-center'>
                  Login per Einmal-Code. Du bekommst eine E-Mail mit dem Code.
                </p>
              </Form>
            )}
          </Formik>
        )}

        {stage === 'ENTER_CODE' && (
          <Formik
            initialValues={{ code: '' }}
            onSubmit={verifyCode}
            validationSchema={codeSchema}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form className='mt-4 space-y-4'>
                <div>
                  <label
                    htmlFor='code'
                    className='block text-sm font-medium text-gray-700 mb-1'
                  >
                    Einmal-Code
                  </label>
                  <Field
                    type='text'
                    inputMode='numeric'
                    name='code'
                    id='code'
                    autoComplete='one-time-code'
                    autoFocus
                    className={inputClass}
                    placeholder='12345'
                  />
                  {errors.code && touched.code ? (
                    <div className='text-xs text-red-600 mt-1'>
                      {errors.code}
                    </div>
                  ) : null}
                  <p className='text-xs text-gray-500 mt-1'>
                    Wir haben dir den Code per E-Mail geschickt.
                  </p>
                </div>

                {error && <div className='text-xs text-red-600'>{error}</div>}

                <Button.PrimaryLG
                  text={isSubmitting ? 'prüfe ...' : 'Anmelden'}
                  fullWidth
                  type='submit'
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />

                <button
                  type='button'
                  onClick={restart}
                  className='block w-full text-xs text-gray-500 hover:text-gray-700 text-center'
                >
                  Andere E-Mail-Adresse verwenden
                </button>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  )
}

export default SignIn
